'use client'
import type { FC, ReactNode } from 'react'
import { memo, useEffect, useState } from 'react'
import Flickity, { type ReactFlickityComponentProps } from 'react-flickity-component'
import type FlickityInstance from 'flickity'

type Props = {
  children: ReactNode,
  className?: string,
  reloadOnUpdate?: boolean,
  dynamicSlider?: boolean,
  elementType?: 'div' | 'section' | 'aside' | 'dd' | 'ul',
  options?: ReactFlickityComponentProps['options'],
  onChange?: (params: unknown) => void
}

const defaultOptions: ReactFlickityComponentProps['options'] = {
  accessibility: false,
  autoPlay: false,
  cellAlign: 'left',
  contain: true,
  draggable: true,
  dragThreshold: 5,
  freeScroll: true,
  freeScrollFriction: 0.075,
  friction: 0.25,
  groupCells: false,
  initialIndex: 0,
  lazyLoad: 2,
  pauseAutoPlayOnHover: true,
  prevNextButtons: false,
  pageDots: false,
  resize: true,
  selectedAttraction: 0.01,
  wrapAround: false
}

const sliderBeingDragged = 'slider-being-dragged'
let originalTouchMoveHandler: ((e: TouchEvent) => boolean) | null = null

function dragEnd() {
  if (originalTouchMoveHandler !== null) {
    document.ontouchmove = originalTouchMoveHandler
  } else {
    document.ontouchmove = null
  }
  setTimeout(() => {
    document.body.classList.remove(sliderBeingDragged)
  }, 50)
}

function dragMove() {
  if (originalTouchMoveHandler === null) {
    originalTouchMoveHandler = document.ontouchmove
  }
  document.ontouchmove = () => false
  document.body.classList.add(sliderBeingDragged)
}

function dragStart() {
  if (originalTouchMoveHandler === null) {
    originalTouchMoveHandler = document.ontouchmove
  }
  document.ontouchmove = () => false
}

export const SliderFlickity: FC<Props> = memo(({ children, className = '', elementType = 'div', options = {}, onChange = null, reloadOnUpdate = false, dynamicSlider = false }) => {
  const [flickityRef, setFlickityRef] = useState<FlickityInstance | null>(null)

  useEffect(() => {
    if (!flickityRef) return
    flickityRef.on('dragStart', dragStart)
    flickityRef.on('dragMove', dragMove)
    flickityRef.on('dragEnd', dragEnd)
    if (onChange) flickityRef.on('change', onChange)

    return () => {
      dragEnd()
      flickityRef.off('dragStart', dragStart)
      flickityRef.off('dragMove', dragMove)
      flickityRef.off('dragEnd', dragEnd)
      if (onChange) flickityRef.off('change', onChange)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flickityRef, onChange])

  return (
    <div className='slider-wrapper'>
      <Flickity
        flickityRef={(ref) => setFlickityRef(ref)}
        className={`slider ${className}`}
        elementType={elementType}
        options={{ ...defaultOptions, ...options }}
        disableImagesLoaded={false}
        reloadOnUpdate={reloadOnUpdate}
        static={!dynamicSlider}
      >
        {children}
      </Flickity>
    </div>
  )
})
